import { ComponentRef, Injectable, Injector, inject, DOCUMENT } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ContextMenuComponent, ContextMenuItem } from './context-menu.component';

export type { ContextMenuItem } from './context-menu.component';

/**
 * Lichte service om een context-menu te openen op een viewport-positie via
 * CDK Overlay. Sluit automatisch bij klik buiten, Esc, of bij item-keuze.
 * Geen `MatMenu` om de bundel-overhead beperkt te houden voor een minimaal
 * menu (uitbreidbaar voor latere items zoals "Toon kanaal-info").
 */
@Injectable({ providedIn: 'root' })
export class ContextMenuService {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);

  private currentOverlayRef: OverlayRef | null = null;
  private currentComponentRef: ComponentRef<ContextMenuComponent> | null = null;
  private outsideClickListener: ((event: MouseEvent) => void) | null = null;
  private escListener: ((event: KeyboardEvent) => void) | null = null;
  private currentOnClose: (() => void) | null = null;

  /**
   * Opent een context-menu op de gegeven viewport-positie. `onClose` (optioneel)
   * wordt aangeroepen bij élke vorm van sluiting: item-keuze, klik buiten,
   * Esc, of een opvolgende `open()` die het huidige menu overschrijft. Zelfde
   * callback maximaal één keer ge\u00efnvokeerd per `open()`-cyclus.
   */
  open(position: { x: number; y: number }, items: readonly ContextMenuItem[], onClose?: () => void): void {
    this.close();
    this.currentOnClose = onClose ?? null;

    const positionStrategy = this.overlay
      .position()
      .global()
      .left(`${position.x}px`)
      .top(`${position.y}px`);

    const overlayRef = this.overlay.create({
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      positionStrategy,
    });

    const portal = new ComponentPortal(ContextMenuComponent, null, this.injector);
    const componentRef = overlayRef.attach(portal);
    componentRef.setInput('items', items);
    componentRef.instance.itemSelected.subscribe(selectedItem => {
      this.close();
      selectedItem.action();
    });

    this.currentOverlayRef = overlayRef;
    this.currentComponentRef = componentRef;

    this.attachDismissListeners(overlayRef);
  }

  close(): void {
    if (!this.currentOverlayRef) return;

    this.detachDismissListeners();
    this.currentOverlayRef.dispose();
    this.currentComponentRef = null;
    this.currentOverlayRef = null;
    const onClose = this.currentOnClose;
    this.currentOnClose = null;
    onClose?.();
  }

  private attachDismissListeners(overlayRef: OverlayRef): void {
    this.outsideClickListener = (event: MouseEvent) => {
      const overlayElement = overlayRef.overlayElement;
      if (!overlayElement.contains(event.target as Node)) {
        this.close();
      }
    };
    this.escListener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.close();
      }
    };
    setTimeout(() => {
      this.document.addEventListener('mousedown', this.outsideClickListener!);
      this.document.addEventListener('contextmenu', this.outsideClickListener!);
    }, 0);
    this.document.addEventListener('keydown', this.escListener);
  }

  private detachDismissListeners(): void {
    if (this.outsideClickListener) {
      this.document.removeEventListener('mousedown', this.outsideClickListener);
      this.document.removeEventListener('contextmenu', this.outsideClickListener);
      this.outsideClickListener = null;
    }
    if (this.escListener) {
      this.document.removeEventListener('keydown', this.escListener);
      this.escListener = null;
    }
  }
}
